import { LoaderFunctionArgs, Outlet } from 'react-router-dom';

import { TProjectViewerLoaderResponse } from '@/console/features/project-viewer/project-viewer.types';
import {
  TCrumb,
  TCustomRouteObject,
  THandle,
} from '@/shared/types/router.types';
import { DeviceFragment } from '@/shared/types/types.generated';

const getJobArtifactsAncestor = (
  data: TProjectViewerLoaderResponse,
): TCrumb | undefined => ({
  label: data.title,
  link: `devices/${data.assetId}`,
});

export const deviceInnerRoutes: TCustomRouteObject[] = [
  {
    path: ':deviceId/:tabId?',
    lazy: async () => {
      const { DeviceRoute } = await import('./device');
      return { Component: DeviceRoute };
    },
    shouldRevalidate: () => false,
    loader: async (args: LoaderFunctionArgs) => {
      const { deviceLoader } = await import('./device');
      return deviceLoader(args);
    },
    handle: {
      crumb: {
        label: (data: DeviceFragment) => data?.name,
      },
    },
  },
  {
    path: ':deviceId/view/:baseJobId',
    lazy: async () => {
      const { JobArtifactsViewRoute } = await import('./job-artifacts-view');
      return { Component: JobArtifactsViewRoute };
    },
    loader: async (args: LoaderFunctionArgs) => {
      const { jobArtifactsViewLoader } = await import('./job-artifacts-view');
      return jobArtifactsViewLoader(args);
    },
    handle: {
      crumb: {
        label: 'view-artifacts',
        localized: true,
        ancestor: getJobArtifactsAncestor,
      },
    },
  },
  {
    path: ':deviceId/compare/:baseJobId/:targetJobId',
    lazy: async () => {
      const { JobArtifactsCompareRoute } =
        await import('./job-artifacts-compare');
      return { Component: JobArtifactsCompareRoute };
    },
    loader: async (args: LoaderFunctionArgs) => {
      const { jobArtifactsCompareLoader } =
        await import('./job-artifacts-compare');
      return jobArtifactsCompareLoader(args);
    },
    handle: {
      crumb: {
        label: 'compare-artifacts',
        localized: true,
        ancestor: getJobArtifactsAncestor,
      },
    },
  },
];

export const deviceRouteHandle: THandle = {
  crumb: {
    label: 'devices',
    localized: true,
  },
  entity: 'Device',
};

export const deviceRoutes: TCustomRouteObject = {
  path: '/devices',
  Component: () => <Outlet />,
  handle: deviceRouteHandle,
  children: [
    {
      index: true,
      lazy: async () => {
        const { DevicesListRoute } = await import('./devices-list');
        return { Component: DevicesListRoute };
      },
    },
    ...deviceInnerRoutes,
  ],
};
